"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import axios from "axios"
import { trackLead } from "@/lib/meta-pixel"
import { useSpamGuard } from "@/components/spam/use-spam-guard"

const contactFormSchema = z.object({
  name: z.string().min(1, "Por favor ingresa tu nombre completo"),
  email: z.string().email("Por favor ingresa un email válido"),
  phone: z.string().min(1, "Por favor ingresa tu teléfono"),
  message: z.string().min(1, "Por favor ingresa un mensaje"),
})

type ContactFormData = z.infer<typeof contactFormSchema>

interface ContactFormProps {
  title?: string;
  description?: string;
}

export function ContactForm({ 
  title = "¿En qué podemos ayudarte?",
  description = "Si tienes alguna duda o necesitas más información, no dudes en contactarnos. Nuestro equipo de profesionales se pondrá en contacto contigo lo antes posible."
}: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const spam = useSpamGuard()

  const { register, handleSubmit, formState: { errors, isValid }, reset } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    if (!spam.ready) {
      toast.error('Por favor completa la verificación de seguridad antes de enviar.')
      return
    }
    setIsSubmitting(true)
    try {
      await axios.post('/api/send-contact-email', {
        ...data,
        ...spam.getSpamPayload(),
      })

      // Track Meta Pixel Lead event
      trackLead('contact_form')

      toast.success('Mensaje envíado')

      // Reset form
      reset()
      spam.reset()
    } catch (error) {
      console.error("Error sending email:", error)
      toast.error('Error al enviar el mensaje. Por favor, inténtalo de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="py-32" style={{ backgroundColor: "#EFEFEF" }} id="contacto">
      <div className="container mx-auto max-w-4xl px-6">
        <div className="flex flex-col gap-4 text-left mb-12">
          <h2 className="text-3xl lg:text-2xl">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl">
            {description}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {spam.fields}
          <FieldGroup className="space-y-6">
            {/* First row: 3 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Field>
                <FieldLabel htmlFor="name" className="text-sm text-gray-800">
                  Nombre
                </FieldLabel>
                <Input
                  id="name"
                  type="text"
                  placeholder="Juan Pérez"
                  {...register("name")}
                  className="text-sm text-gray-900 placeholder:text-gray-500 border border-gray-200 focus-visible:ring-[#1A3043]/30 bg-white"
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="email" className="text-sm text-gray-800">
                  Email
                </FieldLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  {...register("email")}
                  className="text-sm text-gray-900 placeholder:text-gray-500 border border-gray-200 focus-visible:ring-[#1A3043]/30 bg-white"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="phone" className="text-sm text-gray-800">
                  Teléfono
                </FieldLabel>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+34 600 000 000"
                  {...register("phone")}
                  className="text-sm text-gray-900 placeholder:text-gray-500 border border-gray-200 focus-visible:ring-[#1A3043]/30 bg-white"
                />
                {errors.phone && (
                  <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>
                )}
              </Field>
            </div>

            {/* Second row: 1 column */}
            <Field>
              <FieldLabel htmlFor="message" className="text-sm text-gray-800">
                Mensaje
              </FieldLabel>
              <Textarea
                id="message"
                placeholder="Escribe tu mensaje aquí..."
                rows={3}
                {...register("message")}
                className="text-sm text-gray-900 placeholder:text-gray-500 border border-gray-200 focus-visible:ring-[#1A3043]/30 resize-none bg-white"
              />
              {errors.message && (
                <p className="text-xs text-red-500 mt-1">{errors.message.message}</p>
              )}
            </Field>

            {/* Submit button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !spam.ready}
                className="bg-[#1A3043] hover:bg-[#0A3043] text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </FieldGroup>
        </form>
      </div>
    </section>
  )
}

